import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  ArrowLeftRight, TrendingUp, TrendingDown, Wallet, Calendar, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CashFlow() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    fetchCashFlow();
  }, [months]);

  const fetchCashFlow = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/finance/cashflow`, {
        params: { months }
      });
      setData(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load cash flow data'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="cashflow-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Cash Flow</h1>
            <p className="text-muted-foreground mt-1">Monthly income, expenses, and net position summary</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value))}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value={3}>Last 3 months</option>
              <option value={6}>Last 6 months</option>
              <option value={12}>Last 12 months</option>
            </select>
            <Button variant="outline" onClick={fetchCashFlow} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="text-sm text-green-700">Total Income</p>
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(data.cashflow?.reduce((sum, m) => sum + (m.income_net || 0), 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <TrendingDown className="h-8 w-8 text-red-600" />
                  <div>
                    <p className="text-sm text-red-700">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-700">
                      {formatCurrency(data.cashflow?.reduce((sum, m) => sum + (m.expenses_net || 0), 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Calendar className="h-8 w-8 text-purple-600" />
                  <div>
                    <p className="text-sm text-purple-700">Forecast Expenses</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {formatCurrency(data.cashflow?.reduce((sum, m) => sum + (m.forecast_expenses || 0), 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={data.cashflow?.[data.cashflow.length - 1]?.closing_cash >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Wallet className={`h-8 w-8 ${data.cashflow?.[data.cashflow.length - 1]?.closing_cash >= 0 ? 'text-emerald-600' : 'text-orange-600'}`} />
                  <div>
                    <p className={`text-sm ${data.cashflow?.[data.cashflow.length - 1]?.closing_cash >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                      Current Balance
                    </p>
                    <p className={`text-2xl font-bold ${data.cashflow?.[data.cashflow.length - 1]?.closing_cash >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                      {formatCurrency(data.cashflow?.[data.cashflow.length - 1]?.closing_cash)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Monthly Breakdown Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5" />
              Monthly Cash Flow
            </CardTitle>
            <CardDescription>
              Detailed breakdown by month showing income vs expenses
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">
                <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin" />
                Loading cash flow data...
              </div>
            ) : !data?.cashflow || data.cashflow.length === 0 ? (
              <div className="text-center py-12">
                <ArrowLeftRight className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">No cash flow data</h3>
                <p className="text-muted-foreground">Add income and expenses to see your cash flow summary</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Opening</TableHead>
                    <TableHead className="text-right text-green-700">Income</TableHead>
                    <TableHead className="text-right text-red-700">Expenses</TableHead>
                    <TableHead className="text-right text-purple-700">Forecast</TableHead>
                    <TableHead className="text-right">Net Change</TableHead>
                    <TableHead className="text-right">Closing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.cashflow.map((row) => (
                    <TableRow key={row.month}>
                      <TableCell className="font-medium">
                        {formatMonth(row.month)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatCurrency(row.opening_cash)}
                      </TableCell>
                      <TableCell className="text-right text-green-600 font-medium">
                        {formatCurrency(row.income_net)}
                      </TableCell>
                      <TableCell className="text-right text-red-600 font-medium">
                        {formatCurrency(row.expenses_net)}
                      </TableCell>
                      <TableCell className="text-right text-purple-600 font-medium">
                        {formatCurrency(row.forecast_expenses)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={row.net_change >= 0 ? 'default' : 'destructive'} className={row.net_change >= 0 ? 'bg-green-100 text-green-700' : ''}>
                          {formatCurrency(row.net_change)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        <span className={row.closing_cash >= 0 ? 'text-emerald-600' : 'text-orange-600'}>
                          {formatCurrency(row.closing_cash)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Understanding Cash Flow</h4>
                <p className="text-sm text-blue-700 mt-1">
                  <strong>Income:</strong> Revenue from trip payments, contracts, and other sources.<br />
                  <strong>Expenses:</strong> Actual costs recorded (fuel, maintenance, etc.).<br />
                  <strong>Forecast:</strong> Planned recurring or future expenses from your forecast entries.<br />
                  <strong>Net Flow:</strong> Income minus all expenses for the month.<br />
                  <strong>Running Balance:</strong> Cumulative net position over time.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
